// MonitorMoteurManager.jsx - Version adaptée aux couleurs ISS
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { Activity, RefreshCw, Smartphone, CreditCard, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { APP_CONFIG } from '../../../config/appConfig';

const MonitorMoteurManager = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState({
    orangeSms: false,
    mtnSms: false,
    moovSms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkStatus = useCallback(async () => {
    if (!token) {
      setError('Authentification requise');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/monitor/status`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Non autorisé - Veuillez vous reconnecter');
        }
        if (response.status === 403) {
          throw new Error('Accès refusé - Permissions insuffisantes');
        }
        throw new Error(`Erreur HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setStatus({
        orangeSms: data.orangeSms || false,
        mtnSms: data.mtnSms || false,
        moovSms: data.moovSms || false
      });
      toast.success("Statut des moteurs mis à jour");
    } catch (err) {
      console.error("Erreur:", err);
      setError(err.message);
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      checkStatus();
    }
  }, [checkStatus, token]);

  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      checkStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [checkStatus, token]);

  if (!token) {
    return (
      <div className="p-8 text-center font-serif">
        <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-lg p-4">
          <AlertCircle size={24} className="text-[#ef7c00] mx-auto mb-2" />
          <p className="text-[#212529] font-serif">Authentification requise pour voir le statut des moteurs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 font-serif">
      <Toaster position="top-right" />
      
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[#e9ecef] pb-4">
        <div>
          <span className="bg-[#212529] text-white text-[9px] font-serif font-black px-3 py-0.5 rounded-full uppercase tracking-wider inline-block mb-2">
            Real-time Monitor
          </span>
          <h1 className="text-3xl font-serif font-bold text-[#212529] flex items-center gap-3">
            <Activity size={28} className="text-[#009640]" />
            État des Moteurs
          </h1>
          <p className="text-sm text-[#6c757d] font-serif">
            Surveillance en temps réel des moteurs de communication
          </p>
        </div>
        <button 
          onClick={checkStatus} 
          className="p-3 bg-white border border-[#e9ecef] rounded-xl shadow-sm hover:shadow-md hover:bg-[#f8f9fa] transition-all duration-200"
          disabled={loading}
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} style={{ color: '#009640' }} />
        </button>
      </div>

      {error && (
        <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-xl p-4 text-[#212529] text-sm font-serif">
          <strong>Erreur :</strong> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <StatusCard 
          name="Orange SMS Gateway" 
          online={status.orangeSms} 
          icon={Smartphone}
          color="#ef7c00"
          loading={loading}
        />
        <StatusCard 
          name="MTN Mobile Money" 
          online={status.mtnSms} 
          icon={CreditCard}
          color="#009640"
          loading={loading}
        />
        <StatusCard 
          name="Moov Network" 
          online={status.moovSms} 
          icon={Smartphone}
          color="#009640"
          loading={loading}
        />
      </div>
    </div>
  );
};

const StatusCard = ({ name, online, icon, color, loading }) => {
  const IconComponent = icon;
  
  return (
    <div className={`p-0.5 rounded-2xl shadow-lg transition-all duration-300 ${loading ? 'opacity-60' : ''}`} 
         style={{ backgroundColor: online ? '#009640' : '#ef7c00' }}>
      <div className="bg-white rounded-2xl p-6 h-full flex flex-col justify-between min-h-[180px] border border-[#e9ecef]">
        <div className="flex justify-between items-start">
          <div className="p-3 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <IconComponent size={22} style={{ color: color }} />
          </div>
          {online ? (
            <CheckCircle2 size={20} className="text-[#009640]" />
          ) : (
            <XCircle size={20} className="text-[#ef7c00]" />
          )}
        </div>
        
        <div>
          <h3 className="text-sm font-serif font-bold text-[#212529] mb-0.5">{name}</h3>
          <p className={`text-[10px] font-serif font-bold uppercase tracking-wider ${
            loading ? 'text-[#6c757d]' : (online ? 'text-[#009640]' : 'text-[#ef7c00]')
          }`}>
            {loading ? 'Vérification...' : (online ? 'Système Opérationnel' : 'Connexion Interrompue')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MonitorMoteurManager;